import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Loading from "../common/Loading";
import { ACCENT, ROLES, api, publicApi, formatDuration, formatPrice } from "./shared";
import { rememberArtist } from "../fanMembership/fanMembershipApi";
import FollowButton from "../fanHome/FollowButton";
import MusicShell from "../fanHome/MusicShell";
import { isSignedIn } from "../fanHome/fanHomeApi";
import ArtistPlansPanel from "../fanMembership/ArtistPlansPanel";
import FundArtistSheet from "../fanTip/FundArtistSheet";

// Public, read-only artist page (visitors and fans; no login needed). It renders
// only what the public-profile API returns, and has no edit controls.
// The API answers 404 for unknown, private and unverified artists alike.

const SOCIAL_LABELS = { spotify: "Spotify", youtube: "YouTube", instagram: "Instagram", website: "Website" };

// Only http(s) links are rendered as clickable, whatever the API returns.
const safeUrl = (v) => (typeof v === "string" && /^https?:\/\//i.test(v) ? v : null);

function Message({ title, children }) {
  return (
    <div className="text-center py-20 max-w-md mx-auto">
      <h1 className="text-2xl font-bold text-gray-900 mb-2">{title}</h1>
      <div className="text-gray-500">{children}</div>
    </div>
  );
}

// Licensing shown here comes from the artist's Monetization settings (personal / creator / business usage classes).
const USAGE_LABELS = { personal: "Personal use", creator: "Creator", business: "Business" };

export const describeOffer = (o) => {
  const label = USAGE_LABELS[o.license_type] || o.license_type;
  const terms = o.terms || [];
  if (terms.length === 0) return { label, price: formatPrice(o.price, o.currency) };
  if (terms.every((t) => Number(t.price) === 0)) return { label, price: "Free" };
  const parts = terms.map((t) => `${formatPrice(t.price, o.currency)}${t.term === "one_year" ? " / yr" : " lifetime"}`);
  return { label, price: parts.join(" · ") };
};

function TrackRow({ track }) {
  const options = track.license_options || [];
  const duration = formatDuration(track.duration);
  return (
    <li className="rounded-2xl border border-gray-200 bg-white p-4">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <h3 className="font-semibold text-gray-900">{track.title}</h3>
        {track.explicit_content && (
          <span className="rounded border border-gray-300 text-[10px] font-bold px-1.5 py-0.5 text-gray-600" title="Explicit content">E</span>
        )}
        <span className="text-sm text-gray-500 ml-auto">
          {[track.release_date && new Date(track.release_date).getFullYear(), duration].filter(Boolean).join(" · ")}
        </span>
      </div>
      {options.length > 0 && (
        <ul className="mt-3 flex flex-wrap gap-2" aria-label={`Licensing options for ${track.title}`}>
          {options.map((o) => {
            const { label, price } = describeOffer(o);
            return (
              <li key={o.license_type} className="rounded-full bg-gray-50 border border-gray-200 px-3 py-1 text-sm text-gray-800" title={o.description || undefined}>
                <span className="font-semibold">{label}</span>
                <span className="text-gray-500"> · {price}</span>
              </li>
            );
          })}
        </ul>
      )}
    </li>
  );
}

export default function PublicArtistProfile() {
  const { slug } = useParams();
  const [state, setState] = useState({ loading: true, artist: null, notFound: false, failed: false });
  const [showPlans, setShowPlans] = useState(false);
  const [funding, setFunding] = useState(false);
  const [followers, setFollowers] = useState(null); // follower count after this visitor follows / unfollows
  const [tab, setTab] = useState("tracks"); // "tracks" | "about"
  // Slug of the artist the signed-in visitor owns (null = owns none, undefined = not known yet). Used to hide Follow and Fund on one's own page.
  const [ownSlug, setOwnSlug] = useState(undefined);

  const load = useCallback(async () => {
    setState({ loading: true, artist: null, notFound: false, failed: false });
    try {
      const response = await publicApi.get(`public/${encodeURIComponent(slug)}`);
      const artist = response.data?.result || null;
      if (artist) rememberArtist(slug);
      setState({ loading: false, artist, notFound: !artist, failed: false });
    } catch (error) {
      const notFound = error?.response?.status === 404;
      setState({ loading: false, artist: null, notFound, failed: !notFound });
    }
  }, [slug]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    let cancelled = false;
    if (!isSignedIn()) { setOwnSlug(null); return undefined; }
    api.get("status").then(
      (response) => { if (!cancelled) setOwnSlug(response.data?.result?.public_slug || null); },
      () => { if (!cancelled) setOwnSlug(null); } // if this cannot be read the buttons stay; the server still refuses following or funding oneself
    );
    return () => { cancelled = true; };
  }, []);

  const { loading, artist, notFound, failed } = state;
  const isOwnPage = !!(artist && ownSlug && String(ownSlug).toLowerCase() === String(artist.slug || slug).toLowerCase());
  const roleLabel = ROLES.find((r) => r.value === artist?.primary_role)?.label;
  const links = artist ? Object.entries(artist.social_links || {}).filter(([k, v]) => SOCIAL_LABELS[k] && safeUrl(v)) : [];
  const tracks = artist?.tracks || [];

  let body;
  if (loading) {
    body = (
      <div className="flex items-center justify-center py-24" role="status" aria-label="Loading artist">
        <Loading />
      </div>
    );
  } else if (notFound) {
    body = (
      <Message title="Profile not found">
        This artist profile doesn't exist or isn't available.
      </Message>
    );
  } else if (failed || !artist) {
    body = (
      <Message title="We couldn't load this artist">
        <p className="mb-4">Something went wrong on our side. Please try again.</p>
        <button
          type="button"
          onClick={load}
          className="rounded-full font-semibold text-white px-6 py-2.5"
          style={{ backgroundColor: ACCENT }}
        >
          Try again
        </button>
      </Message>
    );
  } else {
    const subline = [artist.handle && `@${artist.handle}`, roleLabel, artist.location].filter(Boolean).join(" · ");
    body = (
      <div className="w-full max-w-3xl mx-auto">
        <div className="rounded-3xl border border-gray-200 bg-white overflow-hidden">
          <div className="h-36 md:h-52" style={{ backgroundColor: "#cdd6d0" }}>
            {safeUrl(artist.cover_image_url) && <img src={artist.cover_image_url} alt="" className="w-full h-full object-cover" />}
          </div>
          <div className="px-5 md:px-8 pb-6 flex items-end gap-5">
            <div className="relative z-10 -mt-12 md:-mt-14 w-28 h-28 md:w-32 md:h-32 rounded-full border-4 border-white overflow-hidden shrink-0 flex items-center justify-center text-4xl font-bold text-white" style={{ backgroundColor: "#93a89b" }}>
              {safeUrl(artist.profile_image_url) ? (
                <img src={artist.profile_image_url} alt={artist.name} className="w-full h-full object-cover" />
              ) : (
                artist.name.charAt(0).toUpperCase()
              )}
            </div>
            <div className="pt-4 min-w-0">
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900 flex flex-wrap items-center gap-3">
                <span className="truncate">{artist.name}</span>
                {artist.verified && (
                  <span className="rounded-full bg-indigo-50 text-indigo-700 text-xs font-semibold px-3 py-1">Verified artist</span>
                )}
              </h1>
              {subline && <p className="text-gray-500">{subline}</p>}
              <p className="text-gray-500 text-sm">
                {followers ?? artist.followers_count} follower{(followers ?? artist.followers_count) === 1 ? "" : "s"}
                {Number.isFinite(Number(artist.members_count)) && (
                  <span data-testid="members-count"> · {Number(artist.members_count)} member{Number(artist.members_count) === 1 ? "" : "s"}</span>
                )}
              </p>
              <div className="mt-3 flex flex-wrap items-start gap-3">
                {ownSlug !== undefined && !isOwnPage && (
                  <>
                    <FollowButton slug={artist.slug || slug} onFollowersChange={setFollowers} />
                    {isSignedIn()
                      ? (
                        <button type="button" onClick={() => setFunding(true)} className="inline-flex items-center justify-center rounded-full border-2 bg-[#e9f4ec] px-6 py-2 text-sm font-semibold leading-normal" style={{ borderColor: ACCENT, color: ACCENT }}>Fund</button>
                      )
                      : <Link to="/login" className="inline-flex items-center justify-center rounded-full border-2 bg-[#e9f4ec] px-6 py-2 text-sm font-semibold leading-normal" style={{ borderColor: ACCENT, color: ACCENT }}>Fund</Link>}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        <section className="mt-5 rounded-2xl border border-gray-200 bg-white p-4 md:p-5" aria-label="Fan membership">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="font-bold text-gray-900">Become a fan</h2>
              <p className="text-sm text-gray-500">Unlimited streaming across Nyawoo. Cancel anytime.</p>
            </div>
            <button
              type="button"
              onClick={() => setShowPlans((v) => !v)}
              aria-expanded={showPlans}
              className="rounded-full font-semibold text-white px-6 py-2.5"
              style={{ backgroundColor: ACCENT }}
            >
              {showPlans ? "Hide plans" : "See plans"}
            </button>
          </div>
          {showPlans && (isSignedIn()
            ? <ArtistPlansPanel slug={artist.slug || slug} />
            : <p className="mt-4 text-sm text-gray-700"><Link to="/login" className="font-semibold underline" style={{ color: ACCENT }}>Log in</Link> to see the plans and become a fan.</p>)}
        </section>

        <div role="tablist" aria-label="Artist page sections" className="mt-6 flex gap-6 border-b border-gray-200">
          {[["tracks", "Tracks"], ["about", "About"]].map(([key, label]) => (
            <button
              key={key}
              type="button"
              role="tab"
              id={`artist-tab-${key}`}
              aria-selected={tab === key}
              aria-controls={`artist-panel-${key}`}
              onClick={() => setTab(key)}
              className={`pb-3 -mb-px text-sm font-semibold border-b-2 ${tab === key ? "text-gray-900" : "border-transparent text-gray-500"}`}
              style={tab === key ? { borderColor: ACCENT } : undefined}
            >
              {label}
            </button>
          ))}
        </div>

        {tab === "tracks" && (
          <section id="artist-panel-tracks" role="tabpanel" aria-labelledby="artist-tab-tracks" className="mt-5" aria-label="Tracks">
            {tracks.length === 0 ? (
              <p className="text-gray-500 rounded-2xl border border-dashed border-gray-200 px-4 py-8 text-center">No public tracks available.</p>
            ) : (
              <ul className="space-y-3">
                {tracks.map((t) => (
                  <TrackRow key={t.id} track={t} />
                ))}
              </ul>
            )}
          </section>
        )}

        {tab === "about" && (
          <section id="artist-panel-about" role="tabpanel" aria-labelledby="artist-tab-about" className="mt-5 space-y-5" aria-label="About">
            {artist.bio
              ? <p className="text-gray-700 whitespace-pre-line">{artist.bio}</p>
              : <p className="text-gray-500 rounded-2xl border border-dashed border-gray-200 px-4 py-8 text-center">This artist hasn't added a bio yet.</p>}
            {artist.genres?.length > 0 && (
              <div className="flex flex-wrap gap-2" aria-label="Genres">
                {artist.genres.map((g) => (
                  <span key={g} className="rounded-full border border-gray-200 bg-white px-3 py-1 text-sm text-gray-700">{g}</span>
                ))}
              </div>
            )}
            <dl className="grid grid-cols-2 gap-3 text-sm">
              {[
                ["Followers", String(followers ?? artist.followers_count ?? 0)],
                Number.isFinite(Number(artist.members_count)) ? ["Members", String(Number(artist.members_count))] : null,
                artist.location ? ["Location", artist.location] : null,
                roleLabel ? ["Role", roleLabel] : null,
                artist.joined_year ? ["On Nyawoo since", String(artist.joined_year)] : null
              ].filter(Boolean).map(([label, value]) => (
                <div key={label} className="rounded-2xl border border-gray-200 bg-white px-4 py-3">
                  <dt className="text-gray-500">{label}</dt>
                  <dd className="font-semibold text-gray-900">{value}</dd>
                </div>
              ))}
            </dl>
            {links.length > 0 && (
              <ul className="flex flex-wrap gap-x-5 gap-y-1">
                {links.map(([k, v]) => (
                  <li key={k}>
                    <a href={v} target="_blank" rel="noopener noreferrer nofollow" className="font-semibold underline" style={{ color: ACCENT }}>
                      {SOCIAL_LABELS[k]}
                    </a>
                  </li>
                ))}
              </ul>
            )}
          </section>
        )}
      </div>
    );
  }

  return (
    <MusicShell>
      {body}
      {funding && state.artist && <FundArtistSheet artist={{ slug: state.artist.slug || slug, name: state.artist.name }} onClose={() => setFunding(false)} />}
    </MusicShell>
  );
}
